/**
 * @file    PartitionsManager.tsx
 * @scope   VIEW
 * @summary Partition selection screen: the partition dropdown, the selected data-source list with
 *          reorder/remove, and entry points into each partition's FieldsManager.
 * @consumes usePartitionsManager, DataSource/FieldsManager models, DataValidationTip
 * @produces `PartitionsManager` component
 * @sideEffects None directly — state/effects live in usePartitionsManager.
 * @notes Presenter half of the Container/Presenter split; keep it declarative.
 */
import { Skeleton, SkeletonVariants } from '@cx/ui';
import Select from 'react-select';
import { PartitionModel, DataSourceItem } from 'models/Enterprise/DataSourceModel';
import { SelectedUserFields } from 'models/Enterprise/FieldsManagerModel';
import './PartitionsManager.scss';
import SortableListItem from '../../../../common/components/SortableListItem/SortableListItem';
import { usePartitionsManager } from './usePartitionsManager';

interface PartitionsManagerProps {
  /** True while the partition catalog is still loading. */
  isLoading?: boolean;
  availableOptions: PartitionModel[];
  selectedValues?: PartitionModel[];
  onChange?: (dataSources: DataSourceItem[]) => void;
  onFieldsManager?: (partitionOption?: PartitionModel) => void;
  selectedEnterpriseFields?: SelectedUserFields[];
}

const customSelectStyles = {
  option: (styles: any) => ({ ...styles }),
  control: (styles: any) => ({ ...styles, maxWidth: '100%' }),
  singleValue: (styles: any) => ({
    ...styles,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
};

/** Placeholder shown while the partition catalog loads. */
const renderSkeleton = (rows: number) => (
  <div className="partitionsManager">
    <Skeleton
      variant={SkeletonVariants.rectangular}
      height="38px"
      className="partitionsManager__skeleton partitionsManager__skeleton--adder"
    />
    <div className="dataSourceContainer">
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton
          key={index}
          variant={SkeletonVariants.rectangular}
          height="80px"
          className="partitionsManager__skeleton partitionsManager__skeleton--item"
        />
      ))}
    </div>
  </div>
);

const PartitionsManager = ({
  isLoading = false,
  availableOptions,
  selectedValues,
  onChange,
  onFieldsManager,
  selectedEnterpriseFields,
}: PartitionsManagerProps) => {
  const {
    dataSources,
    globalState,
    partitionStates,
    showEmptyError,
    handleAddDataSource,
    handleRemoveDataSource,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleBlurAdder,
    getAvailableOptionsForAdd,
    getFieldCountForDataSource,
    isItemInvalid,
  } = usePartitionsManager({
    availableOptions,
    selectedValues,
    onChange,
    selectedEnterpriseFields,
  });

  if (isLoading) {
    return renderSkeleton(selectedValues?.length || 1);
  }

  return (
    <div className="partitionsManager">
      {globalState.AddPartition.visible && (
        <div className="dataSourceAdderContainer">
          <div className="diy-input-container selectInputWrapper">
            <div className="diy-material-textfield">
              <Select
                className="diy-select"
                data-testid="datasource-adder-selector"
                options={getAvailableOptionsForAdd()}
                onChange={handleAddDataSource}
                onBlur={handleBlurAdder}
                value={null}
                placeholder="Select a project"
                isClearable={true}
                styles={customSelectStyles}
                isDisabled={globalState.AddPartition.disabled}
              />
            </div>
            {showEmptyError ? (
              <div className="diy-required-field-wrapper">
                <span className="diy-required-field">
                  You must select at least one project for this chart
                </span>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {dataSources.length > 0 && (
        <div className="dataSourceContainer">
          {dataSources.map((source, index) => {
            const partitionState = partitionStates.get(index);
            if (!partitionState) return null;

            const isInvalid = isItemInvalid(source);

            return (
              <SortableListItem
                key={source.id}
                id={source.id}
                index={index}
                dragState={partitionState.Drag}
                deleteState={partitionState.Delete}
                isInvalid={isInvalid}
                invalidMessage="At least one field is required."
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDelete={handleRemoveDataSource}
              >
                <div className="dataSourceLabel">
                  <span>{source.selectedValue?.label || 'Unknown Data Source'}</span>
                </div>
                <div
                  className={`dataSourceFieldsLabel${
                    isInvalid ? ' dataSourceFieldsLabel--invalid' : ''
                  }`}
                >
                  <span className="dataSourceFieldsCounterLabel">
                    {getFieldCountForDataSource(source.selectedValue)}
                  </span>
                </div>
                {partitionState.ManageFields.visible && (
                  <div className="manage-fields-container">
                    <a
                      href="#"
                      className={`manage-fields-link${
                        partitionState.ManageFields.disabled ? ' manage-fields-link--disabled' : ''
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!partitionState.ManageFields.disabled) {
                          onFieldsManager?.(source.selectedValue || undefined);
                        }
                      }}
                    >
                      Add/Remove data fields
                    </a>
                  </div>
                )}
              </SortableListItem>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PartitionsManager;
